import {
  EmailListFilter,
  EmailMailboxId,
  IEmailMessage,
} from '@dataTypes/email.type'
import {
  EMAIL_PRIMARY_MAILBOXES,
  EMAIL_CUSTOM_FOLDERS,
} from '@constants/Message'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  bulkDeleteEmailMessages,
  bulkUpdateEmailMessages,
  buildFolderPatch,
  buildMailboxPatch,
  fetchEmailMessages,
  syncGoogleInbox,
  updateEmailMessage,
} from '@store/actions/email.action'
import { findStarTagById, getNextStarTagId } from '@utils/email-star.util'
import {
  clearEmailSelection,
  moveEmailsToCustomFolder,
  permanentlyDeleteEmails,
  setEmailActiveCustomFolder,
  setEmailActiveLabel,
  setEmailListFilter,
  setEmailMailbox,
  setEmailMailboxForIds,
  setEmailReadStatus,
  setEmailSelectAll,
  setEmailSortBy,
  setEmailStarTag,
  toggleEmailSelection,
} from '@store/reducers/email.reducer'
import { useCallback, useMemo, useState } from 'react'

const SORT_OPTIONS = ['date', 'subject', 'sender']

const ALL_MAIL_EXCLUDED_MAILBOXES: EmailMailboxId[] = [
  'sent',
  'spam',
  'trash',
  'drafts',
]

const isAllMailMessage = (mailbox: EmailMailboxId): boolean =>
  !ALL_MAIL_EXCLUDED_MAILBOXES.includes(mailbox)

export type UseEmailResult = {
  activeMailbox: EmailMailboxId
  activeCustomFolderId: string | null
  activeLabelId: string | null
  listFilter: EmailListFilter
  sortBy: string
  sortOptions: string[]
  filteredMessages: IEmailMessage[]
  selectedIds: string[]
  mailboxCounts: Record<EmailMailboxId, number>
  folderCounts: Record<string, number>
  labelCounts: Record<string, number>
  primaryMailboxes: typeof EMAIL_PRIMARY_MAILBOXES
  customFolders: typeof EMAIL_CUSTOM_FOLDERS
  foldersOpen: boolean
  labelsOpen: boolean
  setFoldersOpen: (v: boolean) => void
  setLabelsOpen: (v: boolean) => void
  handleMailbox: (key: EmailMailboxId) => void
  handleCustomFolder: (folderId: string | null) => void
  handleCustomLabel: (labelId: string | null) => void
  handleListFilter: (f: EmailListFilter) => void
  handleSortChange: (sort: string) => void
  toggleSelect: (id: string) => void
  toggleStar: (id: string) => void
  toggleSelectAllVisible: () => void
  handleRefresh: () => Promise<void>
  setReadStatus: (ids: string[], isRead: boolean) => void
  moveToMailbox: (ids: string[], mailbox: EmailMailboxId) => void
  moveToCustomFolder: (ids: string[], folderId: string) => void
  permanentlyDelete: (ids: string[]) => void
  allVisibleSelected: boolean
  someVisibleSelected: boolean
  pageCurrent: number
  pageTotal: number
  messagesLoading: boolean
  messagesError: string | null
}

function useEmail(): UseEmailResult {
  const dispatch = useAppDispatch()
  const {
    messages,
    starTags,
    activeMailbox,
    activeCustomFolderId,
    activeLabelId,
    listFilter,
    selectedIds,
    sortBy,
    searchQuery,
    filters,
    messagesLoading,
    messagesError,
  } = useAppSelector((s) => s.email)

  const [foldersOpen, setFoldersOpen] = useState(true)
  const [labelsOpen, setLabelsOpen] = useState(true)

  const reloadMessages = useCallback(async (): Promise<void> => {
    await dispatch(fetchEmailMessages()).unwrap()
  }, [dispatch])

  const syncMutation = useCallback(
    (optimistic: () => void, mutation: () => Promise<unknown>): void => {
      optimistic()
      void mutation().catch(() => {
        void reloadMessages()
      })
    },
    [reloadMessages],
  )

  const mailboxCounts = useMemo((): Record<EmailMailboxId, number> => {
    const base: Record<EmailMailboxId, number> = {
      inbox: 0,
      drafts: 0,
      sent: 0,
      starred: 0,
      all: 0,
      archive: 0,
      spam: 0,
      trash: 0,
    }
    base.all = messages.filter((m) => isAllMailMessage(m.mailbox)).length
    base.starred = messages.filter((m) => m.isStarred).length
    for (const m of messages) {
      if (m.mailbox === 'inbox' && m.folderId) {
        continue
      }
      base[m.mailbox] += 1
    }
    return base
  }, [messages])

  const folderCounts = useMemo((): Record<string, number> => {
    const counts: Record<string, number> = {}
    for (const m of messages) {
      if (m.folderId) {
        counts[m.folderId] = (counts[m.folderId] ?? 0) + 1
      }
    }
    return counts
  }, [messages])

  const labelCounts = useMemo((): Record<string, number> => {
    const counts: Record<string, number> = {}
    for (const m of messages) {
      for (const lb of m.labels) {
        counts[lb.id] = (counts[lb.id] ?? 0) + 1
      }
    }
    return counts
  }, [messages])

  const filteredMessages = useMemo(() => {
    let rows = [...messages]

    if (activeCustomFolderId) {
      rows = rows.filter((m) => m.folderId === activeCustomFolderId)
    } else if (activeLabelId) {
      rows = rows.filter((m) => m.labels.some((lb) => lb.id === activeLabelId))
    } else if (activeMailbox === 'all') {
      rows = rows.filter((m) => isAllMailMessage(m.mailbox))
    } else if (activeMailbox === 'starred') {
      rows = rows.filter((m) => m.isStarred)
    } else {
      rows = rows.filter((m) => m.mailbox === activeMailbox)
      if (activeMailbox === 'inbox') {
        rows = rows.filter((m) => !m.folderId)
      }
    }

    if (listFilter === 'read') rows = rows.filter((r) => r.isRead)
    else if (listFilter === 'unread') rows = rows.filter((r) => !r.isRead)
    else if (listFilter === 'attachments')
      rows = rows.filter((r) => r.hasAttachment)

    const statusFilters = filters.status ?? []
    if (statusFilters.length > 0) {
      rows = rows.filter((r) =>
        statusFilters.every((status) => {
          switch (status) {
            case 'Read':
              return r.isRead
            case 'Unread':
              return !r.isRead
            case 'Starred':
              return r.isStarred
            case 'With attachments':
              return r.hasAttachment
            default:
              return true
          }
        }),
      )
    }

    const labelFilters = filters.labels ?? []
    if (labelFilters.length > 0) {
      rows = rows.filter((r) =>
        r.labels.some((lb) => labelFilters.includes(lb.name)),
      )
    }

    const q = searchQuery.trim().toLowerCase()
    if (q) {
      rows = rows.filter(
        (r) =>
          r.from.toLowerCase().includes(q) ||
          r.subject.toLowerCase().includes(q) ||
          r.snippet.toLowerCase().includes(q),
      )
    }

    const sortKey = sortBy.toLowerCase()
    rows.sort((a, b) => {
      if (sortKey === 'subject') return a.subject.localeCompare(b.subject)
      if (sortKey === 'sender') return a.from.localeCompare(b.from)
      return 0
    })

    return rows
  }, [
    messages,
    activeMailbox,
    activeCustomFolderId,
    activeLabelId,
    listFilter,
    sortBy,
    searchQuery,
    filters,
  ])

  const visibleIds = useMemo(
    () => filteredMessages.map((m) => m.id),
    [filteredMessages],
  )

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])

  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedSet.has(id))

  const someVisibleSelected = visibleIds.some((id) => selectedSet.has(id))

  const toggleSelectAllVisible = useCallback(() => {
    if (allVisibleSelected) {
      dispatch(clearEmailSelection())
    } else {
      dispatch(setEmailSelectAll({ ids: visibleIds, select: true }))
    }
  }, [allVisibleSelected, dispatch, visibleIds])

  const handleMailbox = useCallback(
    (key: EmailMailboxId) => {
      dispatch(setEmailMailbox(key))
    },
    [dispatch],
  )

  const handleCustomFolder = useCallback(
    (folderId: string | null) => {
      dispatch(setEmailActiveCustomFolder(folderId))
    },
    [dispatch],
  )

  const handleCustomLabel = useCallback(
    (labelId: string | null) => {
      dispatch(setEmailActiveLabel(labelId))
    },
    [dispatch],
  )

  const handleListFilter = useCallback(
    (f: EmailListFilter) => {
      dispatch(setEmailListFilter(f))
    },
    [dispatch],
  )

  const handleSortChange = useCallback(
    (sort: string) => {
      dispatch(setEmailSortBy(sort))
    },
    [dispatch],
  )

  const handleRefresh = useCallback(async (): Promise<void> => {
    dispatch(clearEmailSelection())
    // Best-effort Gmail pull; reload picks up whatever synced (backend also
    // syncs on list, so a failure here still shows the latest persisted mail).
    await dispatch(syncGoogleInbox())
      .unwrap()
      .catch(() => undefined)
    await reloadMessages()
  }, [dispatch, reloadMessages])

  const setReadStatus = useCallback(
    (ids: string[], isRead: boolean) => {
      if (ids.length === 0) return
      syncMutation(
        () => dispatch(setEmailReadStatus({ ids, isRead })),
        () =>
          dispatch(
            bulkUpdateEmailMessages({ ids, patch: { is_read: isRead } }),
          ).unwrap(),
      )
    },
    [dispatch, syncMutation],
  )

  const moveToMailbox = useCallback(
    (ids: string[], mailbox: EmailMailboxId) => {
      if (ids.length === 0) return
      syncMutation(
        () => dispatch(setEmailMailboxForIds({ ids, mailbox })),
        () =>
          dispatch(
            bulkUpdateEmailMessages({
              ids,
              patch: buildMailboxPatch(mailbox),
            }),
          ).unwrap(),
      )
    },
    [dispatch, syncMutation],
  )

  const permanentlyDelete = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return
      syncMutation(
        () => dispatch(permanentlyDeleteEmails({ ids })),
        () => dispatch(bulkDeleteEmailMessages({ ids })).unwrap(),
      )
    },
    [dispatch, syncMutation],
  )

  const moveToCustomFolder = useCallback(
    (ids: string[], folderId: string) => {
      if (ids.length === 0) return
      syncMutation(
        () => dispatch(moveEmailsToCustomFolder({ ids, folderId })),
        () =>
          dispatch(
            bulkUpdateEmailMessages({
              ids,
              patch: buildFolderPatch(folderId),
            }),
          ).unwrap(),
      )
    },
    [dispatch, syncMutation],
  )

  const cycleStar = useCallback(
    (id: string) => {
      const row = messages.find((message) => message.id === id)
      const nextStarTagId = getNextStarTagId(row?.starTagId ?? null, starTags)
      const nextStarTag = findStarTagById(starTags, nextStarTagId)
      syncMutation(
        () =>
          dispatch(
            setEmailStarTag({
              id,
              starTagId: nextStarTagId,
              starTag: nextStarTag
                ? {
                    id: nextStarTag.id,
                    name: nextStarTag.name,
                    color: nextStarTag.color,
                  }
                : null,
            }),
          ),
        () =>
          dispatch(
            updateEmailMessage({
              id,
              patch: { star_tag_id: nextStarTagId },
            }),
          ).unwrap(),
      )
    },
    [dispatch, messages, starTags, syncMutation],
  )

  return {
    activeMailbox,
    activeCustomFolderId,
    activeLabelId,
    listFilter,
    sortBy,
    sortOptions: SORT_OPTIONS,
    filteredMessages,
    selectedIds,
    mailboxCounts,
    folderCounts,
    labelCounts,
    primaryMailboxes: EMAIL_PRIMARY_MAILBOXES,
    customFolders: EMAIL_CUSTOM_FOLDERS,
    foldersOpen,
    labelsOpen,
    setFoldersOpen,
    setLabelsOpen,
    handleMailbox,
    handleCustomFolder,
    handleCustomLabel,
    handleListFilter,
    handleSortChange,
    toggleSelect: (id) => dispatch(toggleEmailSelection(id)),
    toggleStar: cycleStar,
    toggleSelectAllVisible,
    handleRefresh,
    setReadStatus,
    moveToMailbox,
    moveToCustomFolder,
    permanentlyDelete,
    allVisibleSelected,
    someVisibleSelected,
    pageCurrent: 1,
    pageTotal: 1,
    messagesLoading,
    messagesError,
  }
}

export default useEmail
